import React, { useEffect, useMemo, useRef, useState } from "react";
import { getUserId } from "utils/auth";
import { getSelectedLanguage } from "utils/language";
import { rewriteDisplayArticle } from "modules/News/api";
import { useTranslation } from "react-i18next";

const DEBOUNCE_MS = 2000; // wait after last change before firing
const REWRITE_PROGRESS_STORAGE_KEY = "df_rewrite_duration_stats_v1";
const DEFAULT_REWRITE_ESTIMATE_MS = 45000;

/**
 * Props:
 * - displayArticleId: number | string (required)
 * - onRewriteResult: function({ title, summary, body }) -> void (required)
 */
const CustomizeArticlePanel = ({ displayArticleId, onRewriteResult }) => {
  const { t } = useTranslation();
  // Single-selects represented as a string; multi-selects as arrays
  const [articleDetail, setArticleDetail] = useState("original");
  const [writingStyle, setWritingStyle] = useState("original");
  const [inclusions, setInclusions] = useState([]); // default none
  const [perspective, setPerspective] = useState(["unbiased"]);

  // Loading overlay while awaiting server
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [rewriteProgress, setRewriteProgress] = useState(8);
  const [rewriteError, setRewriteError] = useState("");

  // Keep last submitted fingerprint to avoid redundant calls
  const lastSubmittedRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const abortRef = useRef(null);
  const progressTimerRef = useRef(null);
  const rewriteStartRef = useRef(0);
  const rewriteEstimateRef = useRef(DEFAULT_REWRITE_ESTIMATE_MS);

  // Normalize payload for backend
  const payload = useMemo(() => {
    const norm = (s) => (s || "").toString().trim().toLowerCase();
    const normList = (arr) => Array.from(new Set((arr || []).map((x) => norm(x)).filter(Boolean))).sort();
    return {
      display_article_id: Number(displayArticleId),
      article_details: norm(articleDetail), // short|original|detailed
      writing_style: norm(writingStyle), // analytical|original|narrative
      inclusions: normList(inclusions), // [interviews|references|narratives]
      perspectives: normList(perspective), // [unbiased|us|china|eu|uae|russia]
      language: undefined,
    };
  }, [displayArticleId, articleDetail, writingStyle, inclusions, perspective]);

  const fingerprint = useMemo(() => JSON.stringify(payload), [payload]);

  const clearDebounce = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  const getProfileKey = useMemo(() => {
    const incl = [...(payload?.inclusions || [])].sort().join(",");
    const persp = [...(payload?.perspectives || [])].sort().join(",");
    return `${payload?.article_details || "original"}|${payload?.writing_style || "original"}|${incl}|${persp}|${getSelectedLanguage() || "de"}`;
  }, [payload]);

  const readStats = () => {
    try {
      const raw = window.localStorage.getItem(REWRITE_PROGRESS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      if (!parsed || typeof parsed !== "object") return { global: null, byKey: {} };
      return {
        global: parsed.global || null,
        byKey: parsed.byKey || {},
      };
    } catch {
      return { global: null, byKey: {} };
    }
  };

  const writeStats = (next) => {
    try {
      window.localStorage.setItem(REWRITE_PROGRESS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Ignore quota/private-mode write issues.
    }
  };

  const updateDurationStats = (durationMs) => {
    if (!Number.isFinite(durationMs) || durationMs <= 0) return;
    const stats = readStats();
    const prevGlobal = stats.global || { avgMs: DEFAULT_REWRITE_ESTIMATE_MS, count: 0 };
    const prevByKey = stats.byKey?.[getProfileKey] || { avgMs: prevGlobal.avgMs, count: 0 };

    // EMA keeps estimates responsive while still stable.
    const alpha = 0.35;
    const nextGlobal = {
      avgMs: Math.max(8000, Math.round((1 - alpha) * prevGlobal.avgMs + alpha * durationMs)),
      count: Math.min((prevGlobal.count || 0) + 1, 1000),
    };
    const nextByKey = {
      avgMs: Math.max(8000, Math.round((1 - alpha) * prevByKey.avgMs + alpha * durationMs)),
      count: Math.min((prevByKey.count || 0) + 1, 1000),
    };

    writeStats({
      global: nextGlobal,
      byKey: {
        ...(stats.byKey || {}),
        [getProfileKey]: nextByKey,
      },
    });
  };

  const getEstimatedDurationMs = () => {
    const stats = readStats();
    const byKey = stats.byKey?.[getProfileKey];
    if (byKey?.avgMs) return byKey.avgMs;
    if (stats.global?.avgMs) return stats.global.avgMs;
    return DEFAULT_REWRITE_ESTIMATE_MS;
  };

  const stopProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const startProgressTimer = () => {
    stopProgressTimer();
    progressTimerRef.current = setInterval(() => {
      const startedAt = rewriteStartRef.current || Date.now();
      const elapsed = Date.now() - startedAt;
      const estimate = rewriteEstimateRef.current || DEFAULT_REWRITE_ESTIMATE_MS;

      let pct = 5 + (elapsed / estimate) * 90; // 5..95 for in-estimate range
      if (elapsed > estimate) {
        const overrunRatio = (elapsed - estimate) / estimate;
        pct = 95 + Math.min(3, overrunRatio * 3); // creep up 95..98 while waiting
      }
      setRewriteProgress((prev) => {
        const bounded = Math.max(prev, Math.min(98, pct));
        return Number.isFinite(bounded) ? bounded : prev;
      });
    }, 250);
  };

    const handleRewrite = async () => {
    if (overlayLoading) return;
    // Abort any in-flight request
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setOverlayLoading(true);
    setRewriteProgress(8);
    setRewriteError("");
    rewriteStartRef.current = Date.now();
    rewriteEstimateRef.current = getEstimatedDurationMs();
    startProgressTimer();
    try {
      const uid = getUserId();
      const json = await rewriteDisplayArticle({
        payload,
        userId: uid ? Number(uid) : undefined,
        language: getSelectedLanguage(),
        signal: controller.signal,
      });
      if (onRewriteResult && typeof onRewriteResult === "function") {
        onRewriteResult({
          title: json?.title,
          summary: json?.summary,
          body: json?.body,
        });
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        setRewriteError(t("news.customize.failed", "Rewrite failed. Please try again."));
        console.warn("CustomizeArticlePanel rewrite error:", e);
      }
    } finally {
      stopProgressTimer();
      const durationMs = Date.now() - rewriteStartRef.current;
      updateDurationStats(durationMs);
      setRewriteProgress(100);
      setOverlayLoading(false);
    }
  };

  // UI handlers
  const toggleOption = (option, setter, state) => {
      const exists = state.includes(option);
      const next = exists ? state.filter((i) => i !== option) : [...state, option];
      setter(next);
    };

  const selectOne = (option, setter) => {
      setter(option);
    };

    // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearDebounce();
      try { abortRef.current && abortRef.current.abort(); } catch {}
      stopProgressTimer();
    };
  }, []);

  return (
    <div className="custom_panel_box position-relative">
      {/* Global overlay when applying changes */}
      {overlayLoading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center px-4"
          style={{
            backdropFilter: "blur(4px)",
            background: "rgba(0,0,0,0.2)",
            zIndex: 5,
            borderRadius: "12px",
          }}
        >
          <span className="small text-light mb-2">{t("news.customize.rewritingArticle")}</span>
          <div
            style={{
              width: "100%",
              maxWidth: "260px",
              height: "8px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.2)",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            <div
              style={{
                width: `${rewriteProgress}%`,
                height: "100%",
                background: "linear-gradient(90deg, #7C3AED 0%, #A855F7 100%)",
                transition: "width 300ms ease",
              }}
            />
          </div>
          <span className="small text-light mt-2">{Math.round(rewriteProgress)}%</span>
        </div>
      )}

      {/* Customize Section */}
      <div className="custom-customize-box bg_glassEffect">
        <h6 className="custom-section-title fw-semibold py-2">
          {t("news.customize.title")}
        </h6>

        {/* Article Details (single select) */}
        <div className="custom-group">
          <label className="custom-label">{t("news.customize.articleDetails")}</label>
          <div className="custom_tab d-flex gap-2  justify-content-between">
            {["short", "original", "detailed"].map((opt) => (
              <span
                key={opt}
                className={`custom-toggle w-100 mw-25 ${
                  articleDetail === opt ? "activetab" : ""
                }`}
                onClick={() => selectOne(opt, setArticleDetail)}
              >
                {t(`news.customize.${opt}`)}
              </span>
            ))}
          </div>
        </div>

        {/* Writing Style (single select) */}
        <div className="custom-group pt-2">
          <label className="custom-label">{t("news.customize.writingStyle")}</label>
          <div className="custom_tab d-flex gap-2  justify-content-between">
            {["analytical", "original", "narrative"].map((opt) => (
              <span
                key={opt}
                className={`custom-toggle w-100 mw-25 ${
                  writingStyle === opt ? "activetab" : ""
                }`}
                onClick={() => selectOne(opt, setWritingStyle)}
              >
                {t(`news.customize.${opt}`)}
              </span>
            ))}
          </div>
        </div>

        {/* Additional Inclusion (multi-select) */}
        <div className="custom-group pt-2">
          <label className="custom-label">{t("news.customize.additionalInclusion")}</label>
          <div className="self_togle_wrap row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-3 g-2">
            {["interviews", "references", "narratives"].map((opt) => (
              <div className="col" key={opt}>
                <span
                  className={`custom-toggle w-100 d-block text-center ${
                    inclusions.includes(opt) ? "activetab" : ""
                  }`}
                  onClick={() => toggleOption(opt, setInclusions, inclusions)}
                >
                  {t(`news.customize.${opt}`)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* View Perspective (single-select) — hidden for now */}
        {false && (
        <div className="custom-group pt-2">
          <label className="custom-label pb-3">{t("news.customize.viewPerspective")}</label>
          <div className="self_togle_wrap row row-cols-1 row-cols-md-2 row-cols-xl-3 g-2">
            {["unbiased", "us", "china", "eu", "uae", "russia"].map((opt) => (
              <div className="col" key={opt}>
                <span
                  className={`custom-toggle w-100 d-block text-center ${
                    perspective.includes(opt) ? "activetab" : ""
                  }`}
                  onClick={() => setPerspective([opt])}
                >
                  {t(`news.customize.${opt}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
        )}
                {/* Rewrite trigger */}
        <div className="pt-3 d-flex justify-content-end custom-toggle d-block text-center">
          <button
            type="button"
            onClick={handleRewrite}
            disabled={overlayLoading}
            style={{
            display: 'block',
            width: '165px',
            height: '34px',
            margin: '0 auto',
            borderRadius: '5px',
            background: '#492366',
            color: '#FFFFFF',
            border: 'none',
            cursor: overlayLoading ? 'default' : 'pointer',
            background: 'rgba(167, 0, 197, 0.29)',
            backdropFilter: 'blur(20px)',
            boxShadow: 'inset 0 0 4px rgba(116, 121, 255, 0.2)',
            padding: '9px 10px',
            fontSize: '12px',
          }}
          >
            {t("news.customize.rewrite")}
          </button>
        </div>
        {rewriteError && (
          <div className="pt-2 text-danger small text-end">{rewriteError}</div>
        )}
      </div>
    </div>
  );
};

export default CustomizeArticlePanel;
